import * as React from 'react';
import { ErrorState, SlaIndicator, SkeletonText, formatDuration, type SlaState } from '@/components/ui';
import { formatDateTime } from '@/lib/date-format';
import { useI18n, resolveTranslation, type TranslationKey } from '@/lib/i18n';
import en from '@/locales/en';

type Translate = (key: TranslationKey, values?: Record<string, string | number>) => string;
const defaultTranslate: Translate = (key, values) => resolveTranslation(en, key).replace(/\{(\w+)\}/g, (m, k) => (values?.[k] === undefined ? m : String(values[k])));

export type SlaMetricName = 'first_response' | 'resolution';

export interface SlaMetricObligation {
  id: string;
  accountId: string;
  ticketId: string;
  cycleId: string;
  metric: SlaMetricName;
  obligationNumber: number;
  state: string;
  targetMinutes: number | null;
  dueAt: string | null;
  warningAt: string | null;
  escalationAt: string | null;
  startedAt: string | null;
  warnedAt: string | null;
  breachedAt: string | null;
  escalatedAt: string | null;
  metAt: string | null;
  pausedAt: string | null;
  stoppedAt: string | null;
  clearedAt: string | null;
  overriddenAt: string | null;
  isOverridden: boolean;
  overrideReason: string | null;
  overrideSource: string | null;
  version: number;
}

export interface SlaPauseInterval {
  id: string;
  accountId: string;
  ticketId: string;
  cycleId: string;
  transitionKey: string;
  statusDefinitionId: string | null;
  statusKey: string | null;
  startedAt: string;
  endedAt: string | null;
  excludedBusinessMinutes: number | null;
}

export interface SlaCycle {
  id: string;
  accountId: string;
  ticketId: string;
  cycleNumber: number;
  state: string;
  startedAt: string;
  completedAt: string | null;
  stoppedAt: string | null;
  source: string;
  correlationId: string;
  policyId: string | null;
  policySnapshot: unknown;
  targetSnapshot: unknown;
  businessHoursSnapshot: unknown;
  obligations: SlaMetricObligation[];
  pauseIntervals: SlaPauseInterval[];
}

export interface SlaLifecycleState {
  cycle: SlaCycle | null;
  obligations: SlaMetricObligation[];
  pauseIntervals: SlaPauseInterval[];
}

type JsonRecord = Record<string, unknown>;

const METRIC_LABEL_KEYS: Record<SlaMetricName, TranslationKey> = {
  first_response: 'tp.sla.firstResponse',
  resolution: 'tp.sla.resolution',
};

const STATE_PRESENTATION: Record<string, SlaState> = {
  not_started: 'not_started',
  running: 'on_track',
  paused: 'paused',
  warned: 'at_risk',
  breached: 'breached',
  escalated: 'escalated',
  met: 'met',
  stopped: 'stopped',
  cleared: 'cleared',
  not_configured: 'not_configured',
  overridden: 'overridden',
  configuration_error: 'configuration_error',
};

function isRecord(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null;
}

function requiredString(value: unknown, field: string): string {
  if (typeof value !== 'string' || value.length === 0) throw new Error(`SLA response is missing ${field}`);
  return value;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function validateObligation(value: unknown, ticketId: string, cycleId: string): SlaMetricObligation {
  if (!isRecord(value)) throw new Error('SLA response contains an invalid metric obligation');
  const obligation = value as Partial<SlaMetricObligation>;
  const metric = obligation.metric;
  if (metric !== 'first_response' && metric !== 'resolution') {
    throw new Error('SLA response contains an inactive metric name');
  }
  if (obligation.ticketId !== ticketId || obligation.cycleId !== cycleId) {
    throw new Error('SLA response contains a stale or mismatched metric obligation');
  }
  return {
    id: requiredString(obligation.id, 'metric identity'),
    accountId: requiredString(obligation.accountId, 'metric account'),
    ticketId,
    cycleId,
    metric,
    obligationNumber: typeof obligation.obligationNumber === 'number' ? obligation.obligationNumber : 1,
    state: requiredString(obligation.state, 'metric state'),
    targetMinutes: typeof obligation.targetMinutes === 'number' ? obligation.targetMinutes : null,
    dueAt: optionalString(obligation.dueAt),
    warningAt: optionalString(obligation.warningAt),
    escalationAt: optionalString(obligation.escalationAt),
    startedAt: optionalString(obligation.startedAt),
    warnedAt: optionalString(obligation.warnedAt),
    breachedAt: optionalString(obligation.breachedAt),
    escalatedAt: optionalString(obligation.escalatedAt),
    metAt: optionalString(obligation.metAt),
    pausedAt: optionalString(obligation.pausedAt),
    stoppedAt: optionalString(obligation.stoppedAt),
    clearedAt: optionalString(obligation.clearedAt),
    overriddenAt: optionalString(obligation.overriddenAt),
    isOverridden: obligation.isOverridden === true,
    overrideReason: optionalString(obligation.overrideReason),
    overrideSource: optionalString(obligation.overrideSource),
    version: typeof obligation.version === 'number' ? obligation.version : 0,
  };
}

function validatePauseInterval(value: unknown, ticketId: string, cycleId: string): SlaPauseInterval {
  if (!isRecord(value)) throw new Error('SLA response contains an invalid pause interval');
  if (value.ticketId !== ticketId || value.cycleId !== cycleId) {
    throw new Error('SLA response contains a stale or mismatched pause interval');
  }
  return {
    id: requiredString(value.id, 'pause identity'),
    accountId: requiredString(value.accountId, 'pause account'),
    ticketId,
    cycleId,
    transitionKey: requiredString(value.transitionKey, 'pause transition'),
    statusDefinitionId: optionalString(value.statusDefinitionId),
    statusKey: optionalString(value.statusKey),
    startedAt: requiredString(value.startedAt, 'pause start time'),
    endedAt: optionalString(value.endedAt),
    excludedBusinessMinutes: typeof value.excludedBusinessMinutes === 'number' ? value.excludedBusinessMinutes : null,
  };
}

/** Validate the account-scoped response before rendering any server data. */
export function validateSlaLifecycleState(value: unknown, ticketId: string): SlaLifecycleState {
  if (!isRecord(value) || !Array.isArray(value.obligations) || !Array.isArray(value.pauseIntervals)) {
    throw new Error('SLA response is incomplete; the displayed clocks may be stale');
  }
  if (value.cycle === null) return { cycle: null, obligations: [], pauseIntervals: [] };
  if (!isRecord(value.cycle)) throw new Error('SLA response contains an invalid cycle');
  const cycleValue = value.cycle as Partial<SlaCycle>;
  if (cycleValue.ticketId !== ticketId) throw new Error('SLA response contains a stale cycle for another ticket');
  const cycleId = requiredString(cycleValue.id, 'cycle identity');
  const obligations = value.obligations.map((item) => validateObligation(item, ticketId, cycleId));
  const pauseIntervals = value.pauseIntervals.map((item) => validatePauseInterval(item, ticketId, cycleId));
  const cycle: SlaCycle = {
    id: cycleId,
    accountId: requiredString(cycleValue.accountId, 'cycle account'),
    ticketId,
    cycleNumber: typeof cycleValue.cycleNumber === 'number' ? cycleValue.cycleNumber : 0,
    state: requiredString(cycleValue.state, 'cycle state'),
    startedAt: requiredString(cycleValue.startedAt, 'cycle start time'),
    completedAt: optionalString(cycleValue.completedAt),
    stoppedAt: optionalString(cycleValue.stoppedAt),
    source: requiredString(cycleValue.source, 'cycle source'),
    correlationId: requiredString(cycleValue.correlationId, 'cycle correlation'),
    policyId: optionalString(cycleValue.policyId),
    policySnapshot: cycleValue.policySnapshot ?? null,
    targetSnapshot: cycleValue.targetSnapshot ?? null,
    businessHoursSnapshot: cycleValue.businessHoursSnapshot ?? null,
    obligations,
    pauseIntervals,
  };
  return { cycle, obligations, pauseIntervals };
}

export function formatSlaCountdown(dueAt: string | null, now = Date.now(), t: Translate = defaultTranslate): string {
  if (!dueAt) return t('tp.sla.noDeadline');
  const due = new Date(dueAt).getTime();
  if (!Number.isFinite(due)) return t('tp.sla.invalidDeadline');
  const difference = due - now;
  if (difference === 0) return t('tp.sla.dueNow');
  return difference > 0
    ? t('tp.sla.dueIn', { duration: formatDuration(difference) })
    : t('tp.sla.overdueBy', { duration: formatDuration(difference) });
}

function valueForObligation(obligation: SlaMetricObligation, now: number, t: Translate): string {
  switch (obligation.state) {
    case 'met': return t('tp.sla.done');
    case 'paused': return t('tp.sla.paused');
    case 'stopped': return t('tp.sla.stopsSla');
    case 'cleared': return t('tp.sla.cleared');
    case 'not_configured': return t('tp.sla.notConfigured');
    case 'not_started': return t('tp.sla.notStarted');
    case 'overridden': return t('tp.sla.overridden');
    case 'configuration_error': return t('tp.sla.configurationError');
    case 'breached': return t('tp.sla.breached');
    case 'escalated': return t('tp.sla.escalated');
    default: return formatSlaCountdown(obligation.dueAt, now, t);
  }
}

function formatSlaDueLine(dueAt: string | null, timezone: string | null | undefined, t: Translate): string | null {
  if (!dueAt || !Number.isFinite(new Date(dueAt).getTime())) return null;
  return t('tp.sla.dueLine', { date: formatDateTime(dueAt, timezone) });
}

function SlaClockRow({ obligation, timezone, now }: { obligation: SlaMetricObligation; timezone?: string | null; now: number }) {
  const { t } = useI18n();
  const state = STATE_PRESENTATION[obligation.state] ?? STATE_PRESENTATION.configuration_error;
  const metricLabel = t(METRIC_LABEL_KEYS[obligation.metric]);
  const value = valueForObligation(obligation, now, t);
  const dueLine = formatSlaDueLine(obligation.dueAt, timezone, t);

  return (
    <article
      aria-label={`${metricLabel}: ${value}`}
      className="rounded-md border border-line bg-surface px-3 py-2.5"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-ink">{metricLabel}</h3>
        </div>
        <SlaIndicator
          state={state}
          value={value}
          className="shrink-0 text-right"
        />
      </div>
      {dueLine && <p className="mt-1 text-xs text-ink-subtle">{dueLine}</p>}
      {obligation.isOverridden && obligation.overrideReason && (
        <p className="mt-2 text-xs text-brand-700">{t('tp.sla.overrideReason', { reason: obligation.overrideReason })}</p>
      )}
    </article>
  );
}

export interface SlaClockPanelProps {
  state: SlaLifecycleState | null;
  loading: boolean;
  error: string | null;
  stale: boolean;
  timezone?: string | null;
  onRetry: () => void;
}

export function SlaClockPanel({ state, loading, error, stale, timezone, onRetry }: SlaClockPanelProps) {
  const { t } = useI18n();
  const [now, setNow] = React.useState(() => Date.now());

  React.useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  if (error) {
    return (
      <ErrorState
        title={stale ? t('tp.sla.mayBeStale') : t('tp.sla.couldNotLoad')}
        message={error}
        onRetry={onRetry}
      />
    );
  }
  if (loading && !state) {
    return <div aria-busy="true" className="space-y-2"><SkeletonText lines={4} /></div>;
  }
  if (!state?.cycle) {
    return <p role="status" className="text-xs text-ink-subtle">{t('tp.sla.noActiveCycle')}</p>;
  }

  const obligations = [...state.obligations].sort((a, b) => (
    a.metric.localeCompare(b.metric) || a.obligationNumber - b.obligationNumber
  ));
  const noPolicy = state.cycle.state === 'no_policy';
  const configurationError = state.cycle.state === 'configuration_error';

  return (
    <div className="space-y-3" aria-live="polite">
      {noPolicy && <p role="status" className="rounded-md border border-line bg-surface-hover px-3 py-2 text-sm text-ink-muted">{t('tp.sla.noPolicy')}</p>}
      {configurationError && <p role="alert" className="rounded-md border border-danger-500/20 bg-danger-50 px-3 py-2 text-sm text-danger-700">{t('tp.sla.configErrorNoDeadline')}</p>}
      {!noPolicy && obligations.length === 0 && (
        <p role="status" className="text-xs text-ink-subtle">{t('tp.sla.noMetrics')}</p>
      )}
      {!noPolicy && obligations.map((obligation) => (
        <SlaClockRow key={obligation.id} obligation={obligation} timezone={timezone} now={now} />
      ))}
      {loading && <p className="text-xs text-ink-subtle" role="status">{t('tp.sla.refreshing')}</p>}
    </div>
  );
}
